import axios, { AxiosError } from 'axios';
import i18n from '../i18n';
import { CreateSessionData, SaveTriviaData, GameSession, ErrorResponse } from '../types';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});

const handleError = (error: unknown): never => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      const data = axiosError.response.data;
      const code = data.code;
      if (code) {
        throw { success: false, code, message: i18n.t(`errors.${code}`, { ns: 'common' }) };
      }
      throw data;
    }
    throw { success: false, message: i18n.t('errors.networkError', { ns: 'common' }) };
  }
  throw { success: false, message: i18n.t('errors.unexpectedError', { ns: 'common' }) };
};

export const createSession = async (data: CreateSessionData): Promise<{ session_id: string }> => {
  try {
    const response = await api.post('/sessions', data);
    return response.data.data;
  } catch (error) {
    return handleError(error);
  }
};

export const getSession = async (sessionId: string): Promise<GameSession> => {
  try {
    const response = await api.get(`/sessions/${sessionId}`);
    return response.data.data;
  } catch (error) {
    return handleError(error);
  }
};

export const saveTrivia = async (sessionId: string, data: SaveTriviaData): Promise<void> => {
  try {
    await api.put(`/sessions/${sessionId}/trivia`, data);
  } catch (error) {
    return handleError(error);
  }
};

export const markRevealSeen = async (sessionId: string): Promise<void> => {
  try {
    await api.put(`/sessions/${sessionId}/reveal`);
  } catch (error) {
    // Don't throw - this is a fire-and-forget analytics call
    console.error('Error marking reveal:', error);
  }
};
